import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { trpc } from "@/lib/trpc";
import { useTranslation } from "@/hooks/useTranslation";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../../../server/routers";
import {
  ArrowLeft,
  ChefHat,
  FlameKindling,
  Globe,
  Lock,
  Printer,
  Scale,
  User,
} from "lucide-react";
import { Link, useRoute } from "wouter";

function formatNumber(value: string | number | null | undefined, decimals = 1) {
  const parsed = typeof value === "number" ? value : parseFloat(value ?? "0");
  return Number.isFinite(parsed) ? parsed.toFixed(decimals) : "0.0";
}

function escapeHtml(value: string | null | undefined) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

type RecipeDetail = NonNullable<inferRouterOutputs<AppRouter>["recept"]["get"]>;

function printRecipe(recipe: RecipeDetail) {
  const rows = recipe.items
    .map(
      item => `<tr>
        <td>${escapeHtml(item.potravina.nazov)}</td>
        <td class="num">${escapeHtml(item.hmotnostG)} g</td>
        <td class="num">${formatNumber(item.potravina.energiaKcal, 1)}</td>
        <td class="num">${formatNumber(item.potravina.proteinG, 1)}</td>
        <td class="num">${formatNumber(item.potravina.tukG, 1)}</td>
        <td class="num">${formatNumber(item.potravina.sacharidG, 1)}</td>
      </tr>`
    )
    .join("");

  const notes = recipe.poznamka
    ? `<section><h2>Notes</h2><p>${escapeHtml(recipe.poznamka)}</p></section>`
    : "";

  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(recipe.nazov)}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111827; margin: 32px; }
    h1 { margin: 0 0 8px; font-size: 28px; }
    h2 { margin: 24px 0 10px; font-size: 18px; }
    .meta { color: #6b7280; margin-bottom: 20px; }
    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 18px 0; }
    .box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
    .label { color: #6b7280; font-size: 12px; text-transform: uppercase; }
    .value { font-size: 18px; font-weight: 700; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th, td { border-bottom: 1px solid #e5e7eb; padding: 8px; text-align: left; }
    th { background: #f9fafb; font-size: 12px; text-transform: uppercase; color: #4b5563; }
    .num { text-align: right; }
    @media print { body { margin: 18mm; } button { display: none; } }
  </style>
</head>
<body>
  <h1>${escapeHtml(recipe.nazov)}</h1>
  <div class="meta">${recipe.jeVerejny ? "Public recipe" : "Private recipe"}</div>
  <section class="summary">
    <div class="box"><div class="label">Total weight</div><div class="value">${formatNumber(recipe.effectiveHmotnostG, 0)} g</div></div>
    <div class="box"><div class="label">Energy</div><div class="value">${formatNumber(recipe.per100g.energiaKcal, 1)} kcal/100g</div></div>
    <div class="box"><div class="label">Protein</div><div class="value">${formatNumber(recipe.per100g.proteinG, 1)} g</div></div>
    <div class="box"><div class="label">Carbs</div><div class="value">${formatNumber(recipe.per100g.sacharidG, 1)} g</div></div>
  </section>
  ${notes}
  <section>
    <h2>Ingredients</h2>
    <table>
      <thead><tr><th>Ingredient</th><th class="num">Weight</th><th class="num">kcal/100g</th><th class="num">Protein</th><th class="num">Fat</th><th class="num">Carbs</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </section>
  <script>window.onload = () => window.print();</script>
</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (!printWindow) return;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}

export default function RecipeDetailPage() {
  const { t } = useTranslation();
  const [, params] = useRoute("/recipes/:id");
  const recipeId = Number(params?.id);
  const isValidRecipeId = Number.isFinite(recipeId) && recipeId > 0;

  const {
    data: recipe,
    isLoading,
    error,
  } = trpc.recept.get.useQuery({ id: recipeId }, { enabled: isValidRecipeId });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <Button asChild variant="ghost" size="sm" className="-ml-3">
              <Link href="/recipes">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("mealLibrary:recept.backToRecipes")}
              </Link>
            </Button>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <ChefHat className="h-7 w-7" />
              {recipe?.nazov ?? t("mealLibrary:recept.detailTitle")}
            </h1>
            <p className="text-muted-foreground">
              {t("mealLibrary:recept.detailSubtitle")}
            </p>
          </div>

          {recipe && (
            <Button onClick={() => printRecipe(recipe)}>
              <Printer className="mr-2 h-4 w-4" />
              {t("mealLibrary:recept.print")}
            </Button>
          )}
        </div>

        {isLoading && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              {t("common:loading")}
            </CardContent>
          </Card>
        )}

        {!isLoading && (!isValidRecipeId || error || !recipe) && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              {t("mealLibrary:recept.notFound")}
            </CardContent>
          </Card>
        )}

        {recipe && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle>{recipe.nazov}</CardTitle>
                      <CardDescription>
                        {recipe.celkovaHmotnostG
                          ? t("mealLibrary:recept.ingredientsWeight", {
                              weight: formatNumber(recipe.sumHmotnostG, 0),
                            })
                          : t("mealLibrary:recept.sumOfIngredients")}
                      </CardDescription>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={recipe.jeVerejny ? "default" : "secondary"}
                      >
                        {recipe.jeVerejny ? (
                          <Globe className="mr-1 h-3 w-3" />
                        ) : (
                          <Lock className="mr-1 h-3 w-3" />
                        )}
                        {recipe.jeVerejny
                          ? t("mealLibrary:recept.public")
                          : t("mealLibrary:recept.private")}
                      </Badge>
                      {recipe.klientAlias != null && (
                        <Badge variant="outline" className="gap-1">
                          <User className="h-3 w-3" />
                          {recipe.klientAlias}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="rounded-lg border p-3">
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <Scale className="h-4 w-4" />
                        {t("mealLibrary:recept.finishedWeight")}
                      </div>
                      <div className="mt-1 text-2xl font-semibold">
                        {formatNumber(recipe.effectiveHmotnostG, 0)} g
                      </div>
                    </div>
                    <div className="rounded-lg border p-3 bg-amber-50">
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <FlameKindling className="h-4 w-4" />
                        {t("mealLibrary:recept.per100g")}
                      </div>
                      <div className="mt-1 text-2xl font-semibold text-amber-600">
                        {formatNumber(recipe.per100g.energiaKcal, 1)} kcal
                      </div>
                    </div>
                    <div className="rounded-lg border p-3 bg-blue-50">
                      <div className="text-sm text-muted-foreground">
                        {t("mealLibrary:recept.protein")}
                      </div>
                      <div className="mt-1 text-2xl font-semibold text-blue-600">
                        {formatNumber(recipe.per100g.proteinG, 1)} g
                      </div>
                    </div>
                    <div className="rounded-lg border p-3 bg-orange-50">
                      <div className="text-sm text-muted-foreground">
                        {t("mealLibrary:recept.fat")}
                      </div>
                      <div className="mt-1 text-2xl font-semibold text-orange-600">
                        {formatNumber(recipe.per100g.tukG, 1)} g
                      </div>
                    </div>
                    <div className="rounded-lg border p-3 bg-teal-50">
                      <div className="text-sm text-muted-foreground">
                        {t("mealLibrary:recept.carbs")}
                      </div>
                      <div className="mt-1 text-2xl font-semibold text-teal-600">
                        {formatNumber(recipe.per100g.sacharidG, 1)} g
                      </div>
                    </div>
                  </div>

                  {recipe.poznamka && (
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="text-sm font-medium">
                        {t("mealLibrary:recept.notes")}
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                        {recipe.poznamka}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar with ingredients list */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>{t("mealLibrary:recept.ingredients")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {recipe.items.map(item => (
                      <div key={item.id} className="rounded-lg border p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">
                              {item.potravina.nazov}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {item.hmotnostG} g
                            </div>
                            <div className="flex gap-2 mt-1 text-xs">
                              <span className="bg-blue-50 text-blue-800 px-2 py-1 rounded border border-blue-200">
                                {formatNumber(item.potravina.proteinG, 1)}g{" "}
                                {t("mealLibrary:recept.protein")}
                              </span>
                              <span className="bg-orange-50 text-orange-800 px-2 py-1 rounded border border-orange-200">
                                {formatNumber(item.potravina.tukG, 1)}g{" "}
                                {t("mealLibrary:recept.fat")}
                              </span>
                              <span className="bg-teal-50 text-teal-800 px-2 py-1 rounded border border-teal-200">
                                {formatNumber(item.potravina.sacharidG, 1)}g{" "}
                                {t("mealLibrary:recept.carbs")}
                              </span>
                            </div>
                          </div>
                          <div className="bg-amber-50 px-2 py-1 rounded text-xs border border-amber-200">
                            {formatNumber(item.potravina.energiaKcal, 1)}{" "}
                            kcal/100g
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
